import { Fragment, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recordings } from '@/api/recordings';
import type { RecordingRow, CallMeToRecordBody } from '@/api/recordings';
import { PageHeader } from '@/components/PageHeader';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { UploadRecordingDialog } from './UploadRecordingDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { cn } from '@/lib/cn';
import {
  Play, Pencil, Trash2, Square, Trash, Phone, Download,
  Plus as PlusIcon, PlusCircle as PlusCircleIcon, Wand2,
} from 'lucide-react';
import { AppendPrependDialog } from './AppendPrependDialog';
import { TtsEditDialog } from './TtsEditDialog';
import { RecordingPicker } from '@/components/ivr/RecordingPicker';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function fmtBytes(raw: string | null): string {
  if (raw == null) return '—';
  const n = Number(raw);
  if (!isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const LANG_LABELS: Record<string, string> = { en: 'EN', he: 'HE', yi: 'YI' };
const LANG_CLASSES: Record<string, string> = {
  en: 'bg-slate-100 text-slate-600',
  he: 'bg-blue-50 text-blue-700',
  yi: 'bg-amber-50 text-amber-700',
};

// Roles that may upload
const CAN_UPLOAD = ['super_admin', 'reseller_owner', 'office_manager', 'standard_user'];
// Roles that may rename / delete
const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

// ── Rename inline dialog ──────────────────────────────────────────────────────

function RenameDialog({ row, onClose }: { row: RecordingRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [newName, setNewName] = useState(row.name);

  const rename = useMutation({
    mutationFn: () => recordings.update(row.id, { name: newName.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordings'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('recordings.renameTitle')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); if (newName.trim()) rename.mutate(); }}
          className="space-y-4"
        >
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t('recordings.renamePlaceholder')}
          />
          {rename.isError && (
            <p role="alert" className="text-xs text-red-500">
              {rename.error instanceof Error ? rename.error.message : t('common.failedToSave')}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={rename.isPending}>{t('common.cancel')}</Button>
            </DialogClose>
            <Button type="submit" disabled={!newName.trim() || rename.isPending}>
              {rename.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete confirm dialog ─────────────────────────────────────────────────────

function DeleteDialog({ row, onClose }: { row: RecordingRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const del = useMutation({
    mutationFn: () => recordings.remove(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordings'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('recordings.deleteTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {t('recordings.deleteBody', { name: row.name })}
        </p>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : t('common.failedToDelete')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={del.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Call-me-to-record dialog ─────────────────────────────────────────────────

type CallMeTargetMode = 'new' | 'append' | 'prepend' | 'overwrite';

function CallMeDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const effectiveCustomerId = useEffectiveCustomerId();
  const [phone, setPhone] = useState('');
  const [mode, setMode] = useState<CallMeTargetMode>('new');
  const [selectedRecordingId, setSelectedRecordingId] = useState<string | null>(null);

  const e164Valid = /^\+[1-9]\d{6,14}$/.test(phone.trim());
  const needsRecording = mode !== 'new';
  const canSubmit =
    e164Valid && (!needsRecording || selectedRecordingId !== null);

  const callMe = useMutation({
    mutationFn: () => {
      const target: CallMeToRecordBody['target'] =
        mode === 'new'
          ? { mode: 'new' }
          : { mode, recording_id: selectedRecordingId! };
      return recordings.callMeToRecord({
        phone_number: phone.trim(),
        target,
      });
    },
    onSuccess: () => {
      // No list invalidation — the recording lands async when the phone
      // call ends. User just gets visual confirmation their call is being
      // dialed. The list auto-refreshes on next focus via react-query.
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('recordings.callMeTitle')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); if (canSubmit) callMe.mutate(); }}
          className="space-y-4"
        >
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-700" htmlFor="callme-phone">
              {t('recordings.callMePhoneLabel')}
            </label>
            <Input
              id="callme-phone"
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
            />
            <p className="text-xs text-slate-500">
              {t('recordings.callMePhoneHint')}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-700" htmlFor="callme-mode">
              {t('recordings.callMeTargetLabel')}
            </label>
            <select
              id="callme-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as CallMeTargetMode)}
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
            >
              <option value="new">{t('recordings.callMeTargetNew')}</option>
              <option value="append">{t('recordings.callMeTargetAppend')}</option>
              <option value="prepend">{t('recordings.callMeTargetPrepend')}</option>
              <option value="overwrite">{t('recordings.callMeTargetOverwrite')}</option>
            </select>
          </div>
          {needsRecording && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">
                {t('recordings.callMeTargetRecording')}
              </label>
              <RecordingPicker
                value={selectedRecordingId}
                onChange={setSelectedRecordingId}
                customerId={effectiveCustomerId}
                hideCreateMenu
              />
            </div>
          )}
          {callMe.isError && (
            <p role="alert" className="text-xs text-red-500">
              {callMe.error instanceof Error ? callMe.error.message : t('common.failedToSave')}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={callMe.isPending}>{t('common.cancel')}</Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit || callMe.isPending}>
              {callMe.isPending ? t('recordings.calling') : t('recordings.callMe')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function RecordingsList() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const accessToken = useAuth((s) => s.accessToken);
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [callMeOpen, setCallMeOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [renameRow, setRenameRow] = useState<RecordingRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<RecordingRow | null>(null);
  const [appendPrepend, setAppendPrepend] = useState<{ row: RecordingRow; mode: 'append' | 'prepend' } | null>(null);
  const [ttsEditRow, setTtsEditRow] = useState<RecordingRow | null>(null);

  // "Only unfiled" toggle — default ON. When ON, list filters to
  // recordings with folder_id IS NULL. When OFF, all recordings show
  // (filed + unfiled). Preference persisted in localStorage so each
  // user gets their own default sticky across sessions.
  const [unfiledOnly, setUnfiledOnlyState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem('recordings:unfiledOnly');
    return raw === null ? true : raw === '1';
  });
  function setUnfiledOnly(v: boolean) {
    setUnfiledOnlyState(v);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('recordings:unfiledOnly', v ? '1' : '0');
    }
  }

  // Debounce search param so we don't fire on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(v: string) {
    setSearch(v);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => setDebouncedSearch(v), 300);
  }

  // Sam 2026-05-20: /portal/recordings defaults to the "Unfiled" view
  // (recordings with folder_id IS NULL). The "Only unfiled" toggle in
  // the header lets users switch to the full list (filed + unfiled).
  // The folder sidebar + per-folder browse lives on /portal/folders.
  const { data, isLoading, error } = useQuery({
    queryKey: ['recordings', debouncedSearch, effectiveCustomerId, unfiledOnly ? 'unfiled' : 'all'],
    queryFn: () => recordings.list({
      ...(unfiledOnly ? { unfiled: true } : {}),
      search: debouncedSearch || undefined,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });

  // Recycle Bin count — separate, lightweight query so the link in the
  // header can show "Recycle Bin (N)". The bin count updates independently
  // of the main list.
  const { data: binData } = useQuery({
    queryKey: ['recordings', 'recycle-bin-count', effectiveCustomerId],
    queryFn: () => recordings.list({
      deleted: true,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const binCount = binData?.rows.length ?? 0;

  const rows = data?.rows ?? [];

  const canUpload = CAN_UPLOAD.includes(user.role);
  const canMutate = CAN_MUTATE.includes(user.role);

  return (
    <>
      <PageHeader
        title={t('recordings.title')}
        description={
          unfiledOnly
            ? "Recordings that aren't in any folder. Toggle off to see all. Use Folders to browse by folder."
            : 'Showing all recordings (filed + unfiled). Toggle on to see only unfiled.'
        }
        actions={
          <div className="flex items-center gap-2">
            <label
              className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer"
              title={unfiledOnly
                ? 'Showing only recordings not in any folder. Click to see all.'
                : 'Showing all recordings. Click to see only unfiled.'}
            >
              <Switch
                checked={unfiledOnly}
                onCheckedChange={setUnfiledOnly}
                aria-label="Only show unfiled recordings"
              />
              <span className="whitespace-nowrap">Only unfiled</span>
            </label>
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('recordings.searchPlaceholder')}
              className="w-52"
              aria-label={t('recordings.searchPlaceholder')}
            />
            {canMutate && (
              <Link
                to="/recordings/recycle-bin"
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
                aria-label={t('recordings.recycleBin')}
              >
                <Trash className="h-4 w-4" />
                <span>{t('recordings.recycleBin')}</span>
                {binCount > 0 && (
                  <span className="ms-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-slate-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
                    {binCount}
                  </span>
                )}
              </Link>
            )}
            {canUpload && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCallMeOpen(true)}
                className="gap-1.5"
              >
                <Phone className="h-4 w-4" />
                {t('recordings.callMeToRecord')}
              </Button>
            )}
            {canUpload && (
              <Button onClick={() => setUploadOpen(true)}>{t('recordings.uploadButton')}</Button>
            )}
          </div>
        }
      />

      {isLoading ? (
        <div className="text-sm text-slate-500">{t('common.loading')}</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : t('recordings.failedToLoad')}
          <Button
            variant="outline"
            size="sm"
            className="ms-3"
            onClick={() => qc.invalidateQueries({ queryKey: ['recordings'] })}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
          {debouncedSearch
            ? t('recordings.noMatch', { query: debouncedSearch })
            : t('recordings.noRecordingsYet')}
        </div>
      ) : (
        <div className="rounded-md border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('recordings.columnName')}</TableHead>
                <TableHead>{t('recordings.columnDuration')}</TableHead>
                <TableHead>{t('recordings.columnLanguage')}</TableHead>
                <TableHead>{t('recordings.columnSize')}</TableHead>
                <TableHead>{t('recordings.columnUploaded')}</TableHead>
                <TableHead className="w-28 text-end">{t('recordings.columnActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      'transition-colors',
                      playingId === row.id && 'bg-blue-50/60',
                    )}
                  >
                    <TableCell className="font-medium">
                      {/* Name + (when playing) inline compact player. Player
                          has a fixed w-72 so the column doesn't balloon
                          across the table when a row is active. */}
                      <div className="flex items-center gap-3 min-w-0">
                        <Link
                          to={`/recordings/REC-${row.ref_number}`}
                          className={cn(
                            'hover:underline hover:decoration-dotted truncate',
                            playingId === row.id && 'max-w-[10rem]',
                          )}
                          title={row.name}
                        >
                          {row.name}
                        </Link>
                        {playingId === row.id && (
                          <div className="w-72 shrink-0">
                            <AudioPlayer
                              src={recordings.audioUrl(row.id, accessToken)}
                              className="bg-white"
                              compact
                            />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums text-slate-500">
                      {fmtDuration(row.duration_ms)}
                    </TableCell>
                    <TableCell>
                      {row.language ? (
                        <span
                          className={cn(
                            'inline-block rounded px-1.5 py-0.5 text-xs font-medium leading-none',
                            LANG_CLASSES[row.language] ?? 'bg-slate-100 text-slate-600',
                          )}
                        >
                          {LANG_LABELS[row.language] ?? row.language.toUpperCase()}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {fmtBytes(row.size_bytes)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-400" title={new Date(row.created_at).toLocaleString()}>
                      {timeAgo(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {/* Play / Stop toggle */}
                        <button
                          type="button"
                          aria-label={playingId === row.id ? t('recordings.stopPlayback') : t('recordings.playRecording')}
                          onClick={() =>
                            setPlayingId((prev) => (prev === row.id ? null : row.id))
                          }
                          className={cn(
                            'rounded p-1.5 transition-colors',
                            playingId === row.id
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                          )}
                        >
                          {playingId === row.id ? (
                            <Square className="h-3.5 w-3.5 fill-current" />
                          ) : (
                            <Play className="h-3.5 w-3.5 fill-current" />
                          )}
                        </button>

                        {/* Download link — uses ?token= so the browser can
                            follow it directly without an Authorization header */}
                        <a
                          href={recordings.audioUrl(row.id, accessToken)}
                          download={row.name}
                          aria-label={t('recordings.download')}
                          title={t('recordings.download')}
                          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>

                        {canMutate && (
                          <>
                            {/* Edit TTS script — pencil icon, only on TTS-
                                sourced recordings that aren't append/prepend
                                derivatives. Opens TtsEditDialog → re-renders
                                audio in place. */}
                            {isTtsSourced(row) && !row.append_base_id && !row.prepend_base_id && (
                              <button
                                type="button"
                                aria-label="Edit TTS script and re-render audio"
                                title="Edit TTS script"
                                onClick={() => setTtsEditRow(row)}
                                className="rounded p-1.5 text-purple-600 transition-colors hover:bg-purple-50 hover:text-purple-700"
                              >
                                <Wand2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {/* Prepend audio to this recording (creates a new
                                derived row; original untouched). */}
                            <button
                              type="button"
                              aria-label={`Prepend audio before ${row.name}`}
                              title="Prepend audio"
                              onClick={() => setAppendPrepend({ row, mode: 'prepend' })}
                              className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            >
                              <PlusCircleIcon className="h-3.5 w-3.5" />
                            </button>
                            {/* Append audio to this recording. */}
                            <button
                              type="button"
                              aria-label={`Append audio after ${row.name}`}
                              title="Append audio"
                              onClick={() => setAppendPrepend({ row, mode: 'append' })}
                              className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            >
                              <PlusIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label={t('recordings.renameTitle')}
                              onClick={() => setRenameRow(row)}
                              className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label={t('common.delete')}
                              onClick={() => setDeleteRow(row)}
                              className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {/* (Inline player now lives inside the Name cell above so
                      the row no longer doubles in height when active.) */}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialogs */}
      <UploadRecordingDialog open={uploadOpen} onOpenChange={setUploadOpen} />
      {callMeOpen && <CallMeDialog onClose={() => setCallMeOpen(false)} />}
      {renameRow && <RenameDialog row={renameRow} onClose={() => setRenameRow(null)} />}
      {deleteRow && <DeleteDialog row={deleteRow} onClose={() => setDeleteRow(null)} />}
      {appendPrepend && (
        <AppendPrependDialog
          base={appendPrepend.row}
          mode={appendPrepend.mode}
          open={true}
          onOpenChange={(v) => { if (!v) setAppendPrepend(null); }}
        />
      )}
      {ttsEditRow && (
        <TtsEditDialog
          row={ttsEditRow}
          open={true}
          onOpenChange={(v) => { if (!v) setTtsEditRow(null); }}
        />
      )}
    </>
  );
}

/**
 * True when the recording was produced via TTS synthesis. Same check
 * as RecordingDetail.tsx; kept local because both call sites are
 * short enough that a shared util would be over-extraction.
 */
function isTtsSourced(row: RecordingRow): boolean {
  return row.source === 'tts' || row.created_via === 'tts';
}
